import React, { useEffect } from 'react';
import { useLatest } from 'ahooks';
import useChart from '@intelligence/v3/_hooks/chart/useChart';
import { LANGUAGE_EN } from '@home/_config/config';
import { getBarChartDynamicOption } from './utils';
import { BAR_CHART_STATIC_OPTION } from './constants';

/**
 * 柱状图组件
 */
const BarChart: React.FC<IBarChartProps> = (props) => {
  const {
    locale = LANGUAGE_EN,
    loading = true,
    data,
    metricName,
    highlightName,
    showLabel = true,
    useXAxisLabelRotate = false,
    xAxisLabelPadding,
    yAxisLabelFormatter,
    valueLabelFormatter,
    nameFormatter,
    tooltipFormatter,
    onBarClick,
    updateDeps,
  } = props;

  // 使用 useLatest 保持最新引用
  const yAxisLabelFormatterLatest = useLatest(yAxisLabelFormatter);
  const valueLabelFormatterLatest = useLatest(valueLabelFormatter);
  const nameFormatterLatest = useLatest(nameFormatter);
  const tooltipFormatterLatest = useLatest(tooltipFormatter);

  // 初始化 chart
  const { chartNode, chartInstance, option, setOption } = useChart({
    locale: locale as string,
    initialOption: BAR_CHART_STATIC_OPTION,
  });

  /**
   * 更新图表配置
   */
  const updateBarChartOption = (chartData?: IBarChartData[] | null) => {
    if (!chartInstance.current || chartInstance.current.isDisposed()) return;
    if (!chartData || chartData.length === 0) {
      setOption({
        ...option,
        // @ts-ignore
        xAxis: { ...option?.xAxis, data: [] },
        series: [],
      });
      return;
    }

    // 生成动态配置
    const dynamicOption = getBarChartDynamicOption({
      data: chartData,
      metricName,
      highlightName,
      showLabel,
      useXAxisLabelRotate,
      xAxisLabelPadding,
      yAxisLabelFormatter: yAxisLabelFormatterLatest.current,
      valueLabelFormatter: valueLabelFormatterLatest.current,
      nameFormatter: nameFormatterLatest.current,
      tooltipFormatter: tooltipFormatterLatest.current,
    });

    // 合并配置
    const newOption = {
      ...option,
      ...dynamicOption,
      tooltip: {
        ...option?.tooltip,
        ...dynamicOption?.tooltip,
      },
      xAxis: {
        ...option?.xAxis,
        ...dynamicOption?.xAxis,
        axisLabel: {
          // @ts-ignore
          ...option?.xAxis?.axisLabel,
          ...dynamicOption?.xAxis?.axisLabel,
        },
      },
      yAxis: dynamicOption?.yAxis,
    };

    setOption(newOption);
  };

  // 数据变化时更新图表
  useEffect(() => {
    if (loading) return;
    updateBarChartOption(data);
  }, [locale, loading, data, metricName, highlightName, showLabel, updateDeps]);

  // 使用 useLatest 保持 onBarClick 最新引用
  const onBarClickLatest = useLatest(onBarClick);

  // 监听柱状图柱子点击事件
  useEffect(() => {
    const chart = chartInstance.current;
    if (!chart || !onBarClickLatest.current) return;

    const handleClick = (params: any) => {
      const clickParams: IBarChartClickParams = {
        name: params?.name || '',
        value: params?.value || '',
        dataIndex: params?.dataIndex || 0,
        seriesIndex: params?.seriesIndex || 0,
        data: data?.[params?.dataIndex] as IBarChartData,
      };
      onBarClickLatest.current?.(clickParams);
    };

    chart.on('click', handleClick);

    return () => {
      chart.off('click', handleClick);
    };
  }, [locale, data]);

  return (
    <div className="dbi-chart-con">
      <div className="dbi-chart-con__wrapper dbi-chart-bar">
        <div className="dbi-chart-con__inner bar-chart-bar-main" ref={chartNode} />
      </div>
    </div>
  );
};

export default BarChart;
